import type { SignupOffer } from '@promotion/types'
import type { UseQueryOptions, UseQueryOverrideOptions, PromotionClient } from '@core/types'
import type { LocationQuery } from '#vue-router'
import { computed, createSharedComposable, isPrefetching, useCookie, useDayjs, useNuxtApp, usePromotionLogger, usePromotionClient, useUserInfo, useRoute, useIsKnownCrawler } from '#imports'

type SignupPromotionsResponse = PromotionClient.components['schemas']['SignupPromotionsResponse']
type PromotionSignupData = PromotionClient.components['schemas']['PromotionSignupData']

export interface UseSignupPromotionsOptions extends Omit<UseQueryOptions<SignupPromotionsResponse>, 'key' | 'enabled' | 'query' | 'service'> {
    /** The route query to read the signup code from; a declaration passes `to.query`. */
    query?: LocationQuery
}

/** The `?code=` signup code, never the auth `code` a MitID or MobilePay return carries. */
function signupCodeOf(query: LocationQuery): string | undefined {
    if (query.mitid || query.mobilepay) return

    const code = Array.isArray(query.code) ? query.code[0] : query.code

    return code || undefined
}

export const useSignupPromotions = createSharedComposable(({ query = useRoute().query, ...options }: UseSignupPromotionsOptions = {}) => {
    const { isLoggedIn } = useUserInfo()
    const { useGetSignupOffer } = usePromotionClient()
    const dayjs = useDayjs()

    const signupBonusCode = useCookie('signup-bonus:code', {
        sameSite: 'lax',
        maxAge: dayjs.duration({ days: 7 }).asSeconds(),
        path: '/',
        secure: true,
        httpOnly: false,
    })

    const signupCode = signupCodeOf(query)

    if (signupCode && !isLoggedIn.value && !isPrefetching(useNuxtApp())) {
        signupBonusCode.value = signupCode
    }

    const logger = usePromotionLogger().withTag('useSignupPromotions')

    const preparedQueryOptions: UseQueryOverrideOptions = {
        lazy: true,
        server: false,
        enabled: () => !isLoggedIn.value,
        ...options,
    }

    // A crawler render lands in the shared crawler cache, so one visitor's cookie code must not pick the offer.
    const storedCode = useIsKnownCrawler().value ? undefined : signupBonusCode.value

    const promotionOfferQuery = useGetSignupOffer(signupCode ?? storedCode ?? undefined, preparedQueryOptions)

    const errorCode = computed(() => promotionOfferQuery.data.value?.error?.errorCode)

    const errorDate = computed(() => {
        const details = promotionOfferQuery.data.value?.error?.details as { startAt?: string, endAt?: string } | undefined
        return details?.endAt
    })

    const activeSignupPromotions = computed<PromotionSignupData[] | []>(() => {
        const promotions = promotionOfferQuery.data.value?.promotions

        if (!promotions?.length) return []

        const featuredPromotion = promotions.find(promotion => promotion.promotionFields?.featured)

        if (!featuredPromotion) {
            if (import.meta.client) {
                logger.error('Featured offer has not been defined, please set the "featured" flag on a signup promotion in Backoffice. Defaulting to the first active signup promotion.')
            }

            const firstPromotion = promotions[0]

            return firstPromotion ? [firstPromotion] : []
        }

        return promotions
    })

    const signupPromotions = computed<SignupOffer[] | []>(() => {
        if (!activeSignupPromotions.value.length) return []

        return activeSignupPromotions.value.reduce((acc: SignupOffer[], promotion) => {
            acc.push({
                ...promotion,
                backgroundColor: promotion?.promotionFields?.backgroundColor,
                declineable: promotion?.promotionFields?.declinable,
                featured: promotion?.promotionFields?.featured,
                imagePath: promotion?.promotionFields?.imagePath,
                longTerms: promotion?.promotionFields?.fullTerms,
                shortTerms: promotion?.promotionFields?.shortTerms,
                showTermsApplySeeBelow: promotion?.promotionFields?.showShortTermsBelowHero,
                signupFlowImagePath: promotion?.promotionFields?.flowImagePath ?? '',
                signupFlowLabel: promotion?.promotionFields?.flowLabel ?? '',
                label: promotion?.promotionFields?.label ?? '',
                subtitle: promotion?.promotionFields?.subtitle,
                title: promotion?.promotionFields?.title,
            })

            return acc
        }, [])
    })

    const defaultSignupPromotion = computed<SignupOffer | undefined>(() => {
        if (!signupPromotions.value?.length) return

        const featuredPromotion = signupPromotions.value.find(promotion => promotion.featured && promotion.declineable)

        if (!featuredPromotion) {
            if (import.meta.client) {
                logger.error('Missing featured offer, please check the configuration for signup promotions in Backoffice. Defaulting to the first signup promotion.')
            }

            return signupPromotions.value.find(promotion => promotion.declineable)
        }

        return featuredPromotion
    })

    return promotionOfferQuery.enrich({
        errorCode,
        errorDate,
        signupPromotions,
        defaultSignupPromotion,
    })
})
